import React, { FC, useMemo, useState } from "react";

import {
	Button,
	ConfirmModal,
	EditableCard,
	Grid,
	SectionCard,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { useProjectActions } from "@/hooks/useProjectActions";

import { TProject, TProjectPayload, TSection } from "@/types";

import { promiseToast } from "@/modules/index";

import { PROJECT_SETTINGS_SECTIONS } from "./CProjectSettingsSections";

interface IProjectSettingsTabProps {
	project: TProject;
	canEdit: boolean;
	canAdmin: boolean;
}

const fieldNamesOf = (sections: TSection[]): string[] =>
	sections.flatMap((section) => section.fields.map((field) => field.name));

type TClientSectionValues = {
	internal?: boolean;
	client?: { id: number } | null;
	rate?: number | null | "";
};

/** The client card's three fields as PATCH takes them; `rate: null` clears the override. */
const clientPayload = (values: TClientSectionValues): TProjectPayload =>
	values.internal
		? { internal: true, rate: null }
		: {
				clientId: values.client?.id ?? null,
				rate:
					values.rate === "" || values.rate === undefined
						? null
						: values.rate,
			};

/**
 * The "Réglages" tab: the project's own fields in an EditableCard
 * (PATCH sends the section's fields only), then the actions that change
 * whether it is open - archive, delete. A project is not moved anywhere
 * any more: it belongs to its client (docs/modele.md, phase 6).
 */
export const ProjectSettingsTab: FC<IProjectSettingsTabProps> = ({
	project,
	canEdit,
	canAdmin,
}) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { archive, remove } = useProjectActions();
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	const sections = useMemo(
		() => PROJECT_SETTINGS_SECTIONS(project.workspace?.id),
		[project.workspace?.id]
	);
	const formValues = {
		...Object.fromEntries(
			fieldNamesOf(sections).map((name) => [
				name,
				(project as unknown as Record<string, unknown>)[name] ?? "",
			])
		),
		client: project.client,
		internal: project.client === null,
		rate: project.rate,
		ticketPrefixes: project.ticketPrefixes ?? [],
	};

	const updateMutation = useMutation({
		mutationFn: (values: TProjectPayload) =>
			ProjectsService.updateProject({ ...values, id: project.id }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "views" ||
					query.queryKey[0] === "projects",
			}),
	});

	const handleSubmit = (section: TSection) => (values: object) => {
		const payload: TProjectPayload =
			section.key === "client"
				? clientPayload(values as TClientSectionValues)
				: Object.fromEntries(
						section.fields.map((field) => [
							field.name,
							(values as Record<string, unknown>)[field.name],
						])
					);
		const prom = updateMutation.mutateAsync(payload);
		promiseToast(prom);
		return prom.then(() => undefined);
	};

	return (
		<Grid columns={2} minItemWidth="400px" gap="1.5rem">
			<div className="flex flex-col gap-6">
				{sections.map((section) => (
					<EditableCard
						key={section.key}
						title={section.title}
						fields={section.fields}
						values={formValues}
						validationSchema={section.validationSchema}
						onSubmit={handleSubmit(section)}
						editInModal
						formikWrapper
						// Archived: read-only until unarchived (the API refuses it).
						readOnly={!canEdit || project.isArchived}
						editColumns={2}
						editLabel={t("actions.edit")}
						submitLabel={t("actions.save")}
						cancelLabel={t("actions.cancel")}
					/>
				))}
			</div>

			{canEdit && (
				<div className="flex flex-col gap-6">
					{canAdmin && (
						<SectionCard
							title={
								project.isArchived
									? t("actions.unarchive")
									: t("actions.archive")
							}
							actions={
								<Button
									size="sm"
									variant="outline"
									picto="archive"
									onClick={() => void archive(project)}
								>
									{project.isArchived
										? t("actions.unarchive")
										: t("actions.archive")}
								</Button>
							}
						>
							<p className="text-sm text-neutral-500">
								{project.isArchived
									? t("projects.settings.unarchiveHint")
									: t("projects.settings.archiveHint")}
							</p>
						</SectionCard>
					)}

					{canAdmin && !project.isArchived && (
						<SectionCard
							title={t("projects.confirmDelete.title")}
							className="border-danger-200"
							actions={
								<Button
									size="sm"
									variant="outline"
									color="danger"
									picto="trash"
									onClick={() => setIsConfirmingDelete(true)}
								>
									{t("actions.delete")}
								</Button>
							}
						>
							<p className="text-sm text-neutral-500">
								{t("projects.settings.deleteHint")}
							</p>
						</SectionCard>
					)}
				</div>
			)}

			<ConfirmModal
				open={isConfirmingDelete}
				onClose={() => setIsConfirmingDelete(false)}
				size="sm"
				title={t("projects.confirmDelete.title")}
				onConfirm={() => {
					setIsConfirmingDelete(false);
					void remove(project).then(() => navigate("/views"));
				}}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			>
				<Trans
					i18nKey="projects.confirmDelete.message"
					values={{ name: project.name }}
					components={{ b: <b /> }}
				/>
			</ConfirmModal>
		</Grid>
	);
};
